import { green } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";

import { topBarHeight } from "./components/navigation/styles";

// Hook that provides styles optimized for MUI v5 sx prop
export const useMainStyles = () => {
  const theme = useTheme();

  return {
    root: {
      display: "flex",
    },
    basicSpacing: {
      margin: theme.spacing(1),
    },
    // Same as basicSpacing but with the left inset zeroed out, for page-root
    // wrappers that need to sit flush against the sidenav border.
    pageRootSpacing: {
      margin: theme.spacing(1),
      marginLeft: 0,
    },
    tableTypography: {
      fontSize: 14,
    },
    tableCellNew: {
      padding: 0,
    },
    content: {
      flexGrow: 1,
      height: "100vh",
      overflow: "auto",
    },
    container: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      paddingLeft: 0,
    },
    // The redesigned pages carry their own headers right at the top, so the
    // page does not need a band of space under the fixed search bar as well.
    containerCompact: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(4),
      paddingLeft: 0,
    },
    spaceSearch: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
      display: "flex",
      gap: theme.spacing(1),
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("xl")]: {
        "& > *": {
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),
          marginLeft: theme.spacing(0.5),
          marginRight: theme.spacing(0.5),
        },
      },
    },
    spaceSearchNoMargin: {
      "& > *": {
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
      },
      [theme.breakpoints.down("xl")]: {
        "& > *": {
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),
          marginLeft: theme.spacing(0.5),
          marginRight: theme.spacing(0.5),
        },
      },
    },
    spaceSearchRetro: {
      "& > *": {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
      },
    },
    spaceDocumentTransferButtons: {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down("lg")]: {
        marginTop: theme.spacing(1),
      },
    },
    checkboxLabel: {
      "& .MuiFormControlLabel-label": {
        marginRight: theme.spacing(1),
      },
    },
    atSymbol: {
      [theme.breakpoints.down("lg")]: {
        position: "relative",
        top: theme.spacing(2),
      },
    },
    paddingTopXSmall: {
      paddingTop: theme.spacing(0.5),
    },
    paddingTopSmall: {
      paddingTop: theme.spacing(1),
    },
    paddingBottomXSmall: {
      paddingBottom: theme.spacing(0.5),
    },
    paddingBottomSmall: {
      paddingBottom: theme.spacing(1),
    },
    padLeft: {
      paddingLeft: theme.spacing(2),
    },
    padLeftLarge: {
      paddingLeft: theme.spacing(4),
    },
    padLeftXSmall: {
      paddingLeft: theme.spacing(0.5),
    },
    spaceLeftXSmall: {
      marginLeft: theme.spacing(0.5),
    },
    spaceLeft: {
      marginLeft: theme.spacing(2),
    },
    spaceRightXSmall: {
      marginRight: theme.spacing(0.5),
    },
    spaceRight: {
      marginRight: theme.spacing(2),
    },
    spaceRightMedium: {
      marginRight: theme.spacing(3),
    },
    spaceRightLarge: {
      marginRight: theme.spacing(4),
    },
    spaceTopXSmall: {
      marginTop: theme.spacing(0.5),
    },
    spaceTopSmall: {
      marginTop: theme.spacing(1),
    },
    spaceTop: {
      marginTop: theme.spacing(2),
    },
    spaceTopLarge: {
      marginTop: theme.spacing(3),
    },
    spaceBottomSmall: {
      marginBottom: theme.spacing(1),
    },
    spaceBottom: {
      marginBottom: theme.spacing(2),
    },
    spaceBottomLarge: {
      marginBottom: theme.spacing(3),
    },
    smallSpacing: {
      margin: 2,
    },
    formControl: {
      minWidth: 240,
    },
    formControlRetro: {
      minWidth: 180,
    },
    textInput: {
      [theme.breakpoints.up("sm")]: {
        minWidth: 150,
      },
      [theme.breakpoints.up("md")]: {
        minWidth: 200,
      },
      [theme.breakpoints.up("lg")]: {
        minWidth: 250,
      },
    },
    textInputLong: {
      [theme.breakpoints.up("sm")]: {
        minWidth: 200,
      },
      [theme.breakpoints.up("md")]: {
        minWidth: 300,
      },
      [theme.breakpoints.up("lg")]: {
        minWidth: 350,
      },
    },
    appBarSpacer: { minHeight: topBarHeight },
    accentGreenBackground: {
      backgroundColor: green[400],
    },
    removeLinkStyle: {
      textDecoration: "none",
      color: "inherit",
    },
    removeVisitedLinkStyle: {
      textDecoration: "none",
      color: "inherit",
      "&:visited": {
        textDecoration: "none",
        color: "inherit",
      },
    },
    coloredLinkStyle: {
      textDecoration: "none",
      color: theme.palette.primary.main,
      "&:visited": {
        textDecoration: "none",
        color: theme.palette.primary.main,
      },
    },
    selector: {
      minWidth: 330,
      marginBottom: theme.spacing(2),
    },
    formSpacing: {
      marginBottom: theme.spacing(3),
      "& > *": {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
      },
    },
    addButton: {
      marginBottom: 3,
    },
    purpleRain500: {
      backgroundColor: "#7A7DFF",
      color: "#FFFFFF",
    },
    purpleRain: {
      backgroundColor: "#6559FF",
      color: "#FFFFFF",
    },
    blueMoon: {
      backgroundColor: "#00b1f2",
      color: "#FFFFFF",
    },
    greenDay: {
      backgroundColor: "#0AAD69",
      color: "#FFFFFF",
    },
    yellowSubmarine: {
      backgroundColor: "#FFA900",
      color: "#FFFFFF",
    },
    orangeCrush: {
      backgroundColor: "#ED6C02",
      color: "#FFFFFF",
    },
    redWine: {
      backgroundColor: "#EA004F",
      color: "#FFFFFF",
    },
    neutral: {
      backgroundColor: "#7A869A",
      color: "#FFFFFF",
    },
    hide: {
      visibility: "hidden",
      display: "none",
    },
    secondaryText: {
      color: theme.palette.secondary.main,
    },
    clickable: {
      "&:hover": {
        cursor: "pointer",
      },
    },
    positionIcon: {
      position: "relative",
      top: 2,
    },
    inlineIcon: {
      position: "relative",
      left: 2,
      bottom: 1,
    },
    shiftRight: {
      position: "relative",
      left: 10,
    },
    tableCell: {
      borderColor: theme.palette.divider,
      borderStyle: "solid",
      paddingBottom: "1.5px",
      paddingTop: "1.5px",
      paddingRight: "10px",
    },
    leftCell: {
      borderWidth: "0px 1px 1px 0px",
      fontWeight: "bold",
      width: "30%",
    },
    leftCell25: {
      borderWidth: "0px 1px 1px 0px",
      fontWeight: "bold",
      width: "25%",
    },
    leftBottomCell: {
      borderWidth: "0px 1px 0px 0px",
      fontWeight: "bold",
    },
    rightCell: {
      borderWidth: "0px 0px 1px 0px",
      width: "70%",
    },
    rightButtonCell: {
      borderWidth: "0px 0px 1px 0px",
      padding: "0px 20px 10px 20px",
      width: "25%",
    },
    rightBottomCell: {
      borderWidth: "0px",
    },
    tableLeftColPadding: {
      padding: "5px 20px 5px 0px",
    },
    tableRightColPadding: {
      padding: "5px 20px",
    },
    toogleButtonColor: {
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: "white",
      },
    },
    confirmDialogAction: {
      padding: "16px 24px",
    },
    featureTableContainer: {
      marginBottom: theme.spacing(2),
      "& .MuiTableCell-root": {
        padding: "16px 24px",
      },
    },
    featureTabContainer: {
      padding: theme.spacing(3),
    },
    saveButtonContainer: {
      marginTop: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    saveButton: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  };
};

// For backward compatibility and gradual migration
const mainStyles = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMainStyles();
};

export default mainStyles;
